import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, MapPin, Heart, BellRing, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

const HIDE_PATHS = [
  "/login", "/register", "/wachtwoord",
  "/welkom", "/dashboard", "/admin", "/instellingen",
  "/billing", "/uitschrijven", "/profiel", "/reacties",
  "/bezichtigingen", "/alerts", "/vault", "/referrals",
  "/makelaar", "/koop/makelaar",
  // Detail pages have a sticky bottom CTA — toast would overlap.
  "/woning/", "/koopwoning/",
];

const ICONS = {
  signup: Sparkles,
  favorite: Heart,
  alert: BellRing,
  scam_check: ShieldCheck,
  premium: Sparkles,
};

const ROTATE_MS = 15000;
const DISMISS_KEY = "wa_live_toast_dismissed_at";
const SUPPRESS_WINDOW_MS = 12 * 60 * 60 * 1000; // 12h after dismiss

const relativeTime = (mins) => {
  if (mins < 1) return "zojuist";
  if (mins < 60) return `${mins} min geleden`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h} uur geleden`;
  const d = Math.floor(h / 24);
  return `${d} dag${d === 1 ? "" : "en"} geleden`;
};

export const LiveToast = () => {
  const loc = useLocation();
  const [events, setEvents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  const suppressed = useMemo(() => {
    if (typeof window === "undefined") return false;
    if (HIDE_PATHS.some((p) => loc.pathname.startsWith(p))) return true;
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < SUPPRESS_WINDOW_MS;
  }, [loc.pathname]);

  useEffect(() => {
    if (suppressed) return;
    let cancelled = false;
    api("/social-proof/live")
      .then((d) => {
        if (cancelled) return;
        const list = (d?.events || []).filter((e) => e.name && e.text);
        setEvents(list);
        if (list.length) {
          setTimeout(() => setVisible(true), 3500); // small delay after page load
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [suppressed]);

  useEffect(() => {
    if (!visible || events.length < 2) return;
    timerRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx((i) => (i + 1) % events.length);
        setVisible(true);
      }, 260);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [visible, events.length]);

  if (suppressed || dismissed || events.length === 0) return null;
  const ev = events[idx] || events[0];
  const Icon = ICONS[ev.action] || Sparkles;

  const close = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div
      data-testid="live-toast"
      role="status"
      aria-live="polite"
      className={`pointer-events-auto fixed bottom-4 left-4 z-40 max-w-[92vw] transition-all duration-300 sm:left-6 sm:bottom-6 sm:max-w-sm ${
        visible ? "translate-y-0 opacity-100" : "translate-y-3 opacity-0"
      }`}
    >
      <div className="flex items-start gap-3 rounded-2xl border border-border bg-white/95 p-3.5 shadow-xl backdrop-blur-sm">
        <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brandblue-light text-brandblue">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-navy">
            <span className="font-bold">{ev.name}</span>
            {ev.city ? <span className="text-slate-500"> · {ev.city}</span> : null}
          </div>
          <p className="mt-0.5 truncate text-sm text-slate-600">{ev.text}</p>
          <div className="mt-1 flex items-center gap-1 text-[11px] font-medium text-slate-400">
            <MapPin size={11} />
            <span>{relativeTime(ev.mins || 5)}</span>
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> LIVE
            </span>
          </div>
        </div>
        <button
          type="button"
          data-testid="live-toast-close"
          onClick={close}
          aria-label="Sluiten"
          className="rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};
